import { DEMO_FIELD_TYPE, DEMO_CALCULATOR_STEP } from 'config/constants'
import {
  DEMO_FILL_ADDITIONAL_FIELDS_ACTION,
  DEMO_PACKAGING_COMPLETE_EVENT,
  DEMO_PRODUCT_COUNTRY_SEARCH_EVENT,
  DEMO_PRODUCT_READY_EVENT,
  DEMO_PRODUCT_SAVE_EVENT,
  DEMO_SAVE_ADDITIONAL_FIELDS_ACTION,
  DEMO_STEP_DELAY,
} from '../../playback-constants'
import {
  animateAction,
  prepareField,
  prepareFields,
  restoreFields,
  wait,
  waitForDocumentElement,
  waitForDocumentElementToDisappear,
  waitForEnabledDocumentElement,
} from '../../helpers'
import { playDemoFields } from '../../play-fields'
import { PackagingFormState } from './invoice-playback'

type ProductStepParams = PackagingFormState & {
  controls: PlaybackControls
  onComplete(): void
  stepElement: HTMLElement
}

type DemoVehicleFieldValues = {
  enginePowerHp: string
  engineType: string
  engineVolumeCc?: string
  manufactureDate: string
}

const getDemoVehicleFieldValues = (modal: HTMLElement): DemoVehicleFieldValues | null => {
  const engineType = modal.dataset.demoEngineType
  const enginePowerHp = modal.dataset.demoEnginePowerHp
  const manufactureDate = modal.dataset.demoManufactureDate

  if (!engineType || !enginePowerHp || !manufactureDate) {
    return null
  }

  const engineVolumeCc = engineType === 'electric'
    ? undefined
    : modal.dataset.demoEngineVolumeCc

  if (engineType !== 'electric' && !engineVolumeCc) {
    return null
  }

  return {
    enginePowerHp,
    engineType,
    engineVolumeCc,
    manufactureDate,
  }
}

const waitForVehicleFields = async (
  vehicleFields: HTMLElement,
  values: DemoVehicleFieldValues,
  controls: PlaybackControls,
) => {
  const expectedNumberFieldsCount = values.engineVolumeCc === undefined ? 1 : 2

  while (!controls.isCancelled()) {
    const numberFields = Array.from(
      vehicleFields.querySelectorAll<HTMLInputElement>('input[type="number"]'),
    )
    const hasExpectedFields = numberFields.length === expectedNumberFieldsCount
    const fieldsHaveValues = numberFields.every(field => field.value !== '')

    if (hasExpectedFields && fieldsHaveValues) {
      return true
    }

    await wait(100)
  }

  return false
}

const animateVehicleFields = async (
  modal: HTMLElement,
  vehicleFields: HTMLElement,
  productFields: AnimatedField[],
  controls: PlaybackControls,
) => {
  const values = getDemoVehicleFieldValues(modal)

  if (!values) {
    return false
  }

  const vehicleFieldsAreReady = await waitForVehicleFields(vehicleFields, values, controls)

  if (!vehicleFieldsAreReady) {
    return false
  }

  const engineTypeControl = vehicleFields.querySelector<HTMLElement>('#engineType[role="combobox"]')
  const numberFields = Array.from(
    vehicleFields.querySelectorAll<HTMLInputElement>('input[type="number"]'),
  )
  const manufactureDateValue = vehicleFields.querySelector<HTMLElement>(
    '.MuiPickersInputBase-sectionsContainer',
  )
  const manufactureDateField = manufactureDateValue?.closest<HTMLElement>('.MuiFormControl-root')
  const previousEngineTypeOpacity = engineTypeControl?.style.opacity || ''
  const previousManufactureDateOpacity = manufactureDateValue?.style.opacity || ''
  const previousManufactureDateTransition = manufactureDateValue?.style.transition || ''

  if (!engineTypeControl || !manufactureDateField) {
    return false
  }

  engineTypeControl.style.opacity = '0'
  manufactureDateValue?.style.setProperty('opacity', '0')
  const numberFieldValues = [
    values.enginePowerHp,
    ...(values.engineVolumeCc === undefined ? [] : [ values.engineVolumeCc ]),
  ]
  const animatedNumberFields = numberFields
    .map((field, index) => prepareField(field, numberFieldValues[index]))
    .filter((field): field is AnimatedField => Boolean(field))

  if (animatedNumberFields.length !== numberFields.length) {
    restoreFields(animatedNumberFields)
    return false
  }

  const visibleFields = [ ...productFields, ...animatedNumberFields ]

  controls.showFields(visibleFields)
  delete document.documentElement.dataset.demoAdditionalFieldsPreparing

  const numberPlaybackFields = numberFields.map((element, index) => ({
    element,
    animatedField: animatedNumberFields[index],
    name: index === 0 ? 'engine_power_hp' : 'engine_volume_cc',
    type: DEMO_FIELD_TYPE.TEXTFIELD as const,
    value: numberFieldValues[index],
  }))

  const fieldsAreComplete = await playDemoFields({
    controls,
    fields: [
      {
        element: engineTypeControl,
        name: 'engine_type',
        onChoose: () => {
          engineTypeControl.style.opacity = previousEngineTypeOpacity
        },
        type: DEMO_FIELD_TYPE.SELECT,
        value: values.engineType,
      },
      ...numberPlaybackFields,
      {
        element: manufactureDateField,
        name: 'manufacture_date',
        onChoose: () => {
          manufactureDateValue?.style.setProperty('opacity', '1')
        },
        type: DEMO_FIELD_TYPE.DATE,
        value: values.manufactureDate,
      },
    ],
    step: DEMO_CALCULATOR_STEP.PACKAGING,
    visibleFields,
  })

  engineTypeControl.style.opacity = previousEngineTypeOpacity

  if (manufactureDateValue) {
    manufactureDateValue.style.transition = 'opacity 180ms ease'
    manufactureDateValue.style.opacity = '1'
    await wait(180)
    manufactureDateValue.style.opacity = previousManufactureDateOpacity
    manufactureDateValue.style.transition = previousManufactureDateTransition
  }

  restoreFields(animatedNumberFields)
  controls.showFields(productFields)

  return fieldsAreComplete
}

const animateModalFields = async (
  modal: HTMLElement,
  productFields: AnimatedField[],
  controls: PlaybackControls,
) => {
  const vehicleFields = modal.querySelector<HTMLElement>('.vehicle-fields')

  if (vehicleFields) {
    return animateVehicleFields(modal, vehicleFields, productFields, controls)
  }

  const modalFields = prepareFields(modal)

  if (!modalFields.length) {
    return true
  }

  controls.showFields([ ...productFields, ...modalFields ])
  await controls.typeFields(modalFields)
  restoreFields(modalFields)
  controls.showFields(productFields)

  return true
}

const selectExciseRate = async (
  modal: HTMLElement,
  controls: PlaybackControls,
) => {
  const radioGroup = modal.querySelector<HTMLElement>(
    '[data-demo-playback-group="additional-fields-radio"]',
  )

  if (!radioGroup) {
    return true
  }

  let selectedField: HTMLInputElement | null = null

  while (!controls.isCancelled() && !selectedField) {
    const exciseRateIndex = modal.dataset.demoExciseRateIndex

    if (exciseRateIndex !== undefined) {
      selectedField = radioGroup.querySelector<HTMLInputElement>(
        `[data-demo-playback-radio="${exciseRateIndex}"] input`,
      )
    }

    if (!selectedField) {
      await wait(100)
    }
  }

  if (!selectedField || !radioGroup) {
    return false
  }

  return playDemoFields({
    controls,
    fields: [
      {
        element: radioGroup,
        name: 'excise_rate',
        optionSelector: `[data-demo-playback-radio="${modal.dataset.demoExciseRateIndex}"] input`,
        type: DEMO_FIELD_TYPE.RADIO,
        value: selectedField.value,
      },
    ],
    step: DEMO_CALCULATOR_STEP.PACKAGING,
  })
}

const playAdditionalFields = async (
  action: HTMLElement,
  productFields: AnimatedField[],
  controls: PlaybackControls,
): Promise<AnimatedField[] | null> => {
  await wait(DEMO_STEP_DELAY)
  await animateAction(action)

  document.documentElement.dataset.demoAdditionalFieldsPreparing = 'true'
  action.click()

  const modal = await waitForDocumentElement('.packaging-additional', controls.isCancelled)

  if (!modal) {
    delete document.documentElement.dataset.demoAdditionalFieldsPreparing
    return null
  }

  await waitForDocumentElementToDisappear('.packaging-additional__loading', controls.isCancelled)

  const fieldsAreComplete = await animateModalFields(modal, productFields, controls)

  delete document.documentElement.dataset.demoAdditionalFieldsPreparing

  if (!fieldsAreComplete) {
    return null
  }

  const exciseRateIsSelected = await selectExciseRate(modal, controls)

  if (!exciseRateIsSelected) {
    return null
  }

  const saveAction = await waitForEnabledDocumentElement(
    `[data-demo-playback-action="${DEMO_SAVE_ADDITIONAL_FIELDS_ACTION}"]`,
    controls.isCancelled,
  )

  if (!saveAction) {
    return null
  }

  await animateAction(saveAction)

  const preservedFields = productFields.map(field => ({
    ...field,
    visibleValue: field.targetValue,
  }))

  controls.showFields(preservedFields)
  await wait(50)

  saveAction.click()
  await waitForDocumentElementToDisappear('.packaging-additional', controls.isCancelled)
  await wait(DEMO_STEP_DELAY)

  return preservedFields
}

const waitForContainerValues = async (
  containersForm: HTMLElement,
  controls: PlaybackControls,
) => {
  while (!controls.isCancelled()) {
    const inputs = Array.from(containersForm.querySelectorAll<HTMLInputElement>('input'))
    const hasInputs = Boolean(inputs.length)
    const inputsHaveValues = inputs.every(input => input.value !== '')

    if (hasInputs && inputsHaveValues) {
      return true
    }

    await wait(100)
  }

  return false
}

const selectDeliveryMethod = async (
  productFields: AnimatedField[],
  controls: PlaybackControls,
) => {
  const deliveryMethodSelect = await waitForDocumentElement(
    '[data-demo-playback-action="delivery-method-select"]',
    controls.isCancelled,
  )

  if (!deliveryMethodSelect) {
    return false
  }

  const deliveryMethodCombobox = deliveryMethodSelect.querySelector<HTMLElement>('[role="combobox"]') ||
    deliveryMethodSelect
  const deliveryMethodTarget = deliveryMethodSelect.dataset.demoDeliveryMethodTarget || '2'
  const isContainerDelivery = deliveryMethodTarget === '2'

  const deliveryMethodIsSelected = await playDemoFields({
    controls,
    fields: [
      {
        element: deliveryMethodCombobox,
        name: 'delivery_method',
        optionSelector: `[data-demo-delivery-method="${deliveryMethodTarget}"]`,
        type: DEMO_FIELD_TYPE.SELECT,
        value: deliveryMethodTarget,
      },
    ],
    step: DEMO_CALCULATOR_STEP.PACKAGING,
    visibleFields: productFields,
  })

  if (!deliveryMethodIsSelected) {
    return false
  }

  let containerFields: AnimatedField[] = []

  if (isContainerDelivery) {
    const containersForm = await waitForDocumentElement('.packaging-edit-containers__fields', controls.isCancelled)

    if (containersForm) {
      const containerValuesAreReady = await waitForContainerValues(containersForm, controls)

      if (containerValuesAreReady) {
        containerFields = prepareFields(containersForm).map(field => ({
          ...field,
          visibleValue: field.targetValue,
        }))
        controls.showFields([ ...productFields, ...containerFields ])
        controls.setActiveFieldElement(null)
        await wait(DEMO_STEP_DELAY)
      }
    }
  }

  const confirmDeliveryMethod = await waitForDocumentElement(
    '[data-demo-playback-action="confirm-delivery-method"]',
    controls.isCancelled,
  )

  if (confirmDeliveryMethod) {
    await animateAction(confirmDeliveryMethod)
    confirmDeliveryMethod.click()
    await waitForDocumentElementToDisappear(
      '[data-demo-playback-action="confirm-delivery-method"]',
      controls.isCancelled,
    )
  }

  restoreFields(containerFields)
  controls.showFields(productFields)

  return true
}

const playProductFields = (
  fields: AnimatedField[],
  controls: PlaybackControls,
) => playDemoFields({
  controls,
  fields: fields.map(field => {
    const isCountryField = Boolean(field.element.closest('.add-product__country_code'))

    if (isCountryField) {
      return {
        animatedField: field,
        element: field.element,
        name: field.element.name,
        onType: value => controls.dispatchEvent(new CustomEvent(DEMO_PRODUCT_COUNTRY_SEARCH_EVENT, {
          detail: { query: value },
        })),
        optionSelector: '.MuiAutocomplete-popper [role="option"]',
        type: DEMO_FIELD_TYPE.AUTOCOMPLETE as const,
        value: field.targetValue.slice(0, 3),
      }
    }

    return {
      animatedField: field,
      element: field.element,
      name: field.element.name,
      type: DEMO_FIELD_TYPE.TEXTFIELD as const,
      value: field.targetValue,
    }
  }),
  step: DEMO_CALCULATOR_STEP.PACKAGING,
  visibleFields: fields,
})

export const playProductStep = async ({
  controls,
  fields,
  onComplete,
  remainingFields,
  saveProductAction,
  stepElement,
}: ProductStepParams): Promise<boolean> => {
  const hasTypedRemainingFields = await playProductFields(remainingFields, controls)

  if (!hasTypedRemainingFields || !saveProductAction) {
    controls.clearFields(fields)
    return false
  }

  let preservedProductFields = fields

  const fillAdditionalFieldsAction = stepElement.querySelector<HTMLElement>(
    `[data-demo-playback-action="${DEMO_FILL_ADDITIONAL_FIELDS_ACTION}"]`,
  )

  if (fillAdditionalFieldsAction) {
    const savedFields = await playAdditionalFields(fillAdditionalFieldsAction, fields, controls)

    if (!savedFields) {
      controls.clearFields(fields)
      return false
    }

    preservedProductFields = savedFields
  }

  await wait(DEMO_STEP_DELAY)
  controls.setActiveFieldElement(null)

  if (!controls.dispatchEvent(new CustomEvent(DEMO_PRODUCT_READY_EVENT))) {
    return false
  }

  await wait(250)
  await animateAction(saveProductAction)
  controls.dispatchEvent(new CustomEvent(DEMO_PRODUCT_SAVE_EVENT))
  await wait(DEMO_STEP_DELAY)

  let visibleProductFields: AnimatedField[] = []

  if (preservedProductFields.length) {
    visibleProductFields = preservedProductFields
  }

  controls.showFields(visibleProductFields)

  const deliveryMethodIsSelected = await selectDeliveryMethod(visibleProductFields, controls)

  if (!deliveryMethodIsSelected) {
    controls.clearFields(visibleProductFields)
    return false
  }

  controls.clearFields(visibleProductFields)

  if (!controls.dispatchEvent(new CustomEvent(DEMO_PACKAGING_COMPLETE_EVENT))) {
    return false
  }

  onComplete()

  return true
}
